<template>
  <div class="app-page box-card member-page" v-loading="loading">
    <div class="box-card-header">
      <div class="title">{{ $t('membershipCenter') }}</div>
    </div>

    <div class="box-card-content">
      <el-alert
        v-if="Number(info.enabled) !== 1"
        :title="$t('membershipDisabled')"
        type="warning"
        :closable="false"
        show-icon
      />

      <div class="member-card">
        <div>
          <div class="small">{{ $t('membershipCurrentLevel') }}</div>
          <div class="level">{{ memberLevelText }}</div>
        </div>
        <div>
          <div class="small">{{ $t('membershipCashBalance') }}</div>
          <div class="amount">{{ info.cash_balance || '0.00' }} MEQ</div>
        </div>
        <div v-if="Number(info.member_expire_time) > 0">
          <div class="small">{{ $t('membershipExpireTime') }}</div>
          <div class="expire">{{ formatTime(info.member_expire_time) }}</div>
        </div>
      </div>

      <div class="plan" v-if="Number(info.enabled) === 1">
        <div>
          <h3>{{ $t('membershipPremium') }}</h3>
          <div class="price">
            {{ info.price || '0.00' }} MEQ
            <span v-if="Number(info.days) > 0">/ {{ info.days }} {{ $t('membershipDays') }}</span>
            <span v-else>/ {{ $t('membershipPermanent') }}</span>
          </div>
          <div class="benefits" v-if="info.benefits">{{ info.benefits }}</div>
        </div>
        <el-button type="primary" :loading="buying" @click="buy">
          {{ Number(info.member_level) === 1 ? $t('membershipRenew') : $t('membershipUpgrade') }}
        </el-button>
      </div>

      <div class="orders">
        <div class="section-title">{{ $t('membershipOrders') }}</div>
        <el-table :data="orders.list" border>
          <el-table-column prop="order_no" :label="$t('membershipOrderNo')" min-width="180" />
          <el-table-column prop="amount" :label="$t('membershipPaidAmount')" min-width="130" />
          <el-table-column :label="$t('membershipPeriod')" min-width="120">
            <template slot-scope="scopeData">
              {{ Number(scopeData.row.member_days) > 0 ? `${scopeData.row.member_days} ${$t('membershipDays')}` : $t('membershipPermanent') }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('membershipPurchaseTime')" min-width="170">
            <template slot-scope="scopeData">{{ formatTime(scopeData.row.create_time) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MembershipCenter',
  data () {
    return {
      loading: false,
      buying: false,
      info: {},
      orders: {
        list: [],
        total: 0
      }
    }
  },
  computed: {
    memberLevelText () {
      return Number(this.info.member_level) === 1
        ? this.$t('promotionPremiumMember')
        : this.$t('promotionNormalMember')
    }
  },
  created () {
    this.load()
  },
  methods: {
    load () {
      this.loading = true
      Promise.all([
        this.$http.get('/api/membership/info'),
        this.$http.post('/api/membership/orders', { page: 1, size: 50 })
      ]).then(([info, orders]) => {
        this.info = info || {}
        this.orders = orders || { list: [], total: 0 }
      }).finally(() => {
        this.loading = false
      })
    },
    buy () {
      const message = this.$t('membershipConfirmPurchase', {
        price: this.info.price || '0.00'
      })
      this.$confirm(message, this.$t('membershipConfirmTitle'), {
        type: 'warning'
      }).then(() => {
        this.buying = true
        return this.$http.post('/api/membership/purchase').then(() => {
          this.$message.success(this.$t('membershipPurchaseSuccess'))
          this.load()
        }).finally(() => {
          this.buying = false
        })
      }).catch(() => {})
    },
    formatTime (time) {
      if (!time) return '-'
      const timestamp = Number(time)
      const date = new Date(timestamp < 1000000000000 ? timestamp * 1000 : timestamp)
      return date.toLocaleString()
    }
  }
}
</script>

<style scoped lang="scss">
.member-page {
  min-height: calc(100vh - 90px);
  margin: 0 -38px;
  padding: 0 38px 38px;
  color: #d7edf4;
  background: #061019;

  .box-card-header { padding-top: 18px; margin-bottom: 14px; }
  .box-card-header .title { color: #00c8f4; font-size: 18px; font-weight: 600; }
  .box-card-content { background: transparent; }

  .member-card,
  .plan,
  .orders {
    border: 1px solid rgba(31, 146, 176, .35);
    border-radius: 7px;
    background: linear-gradient(180deg, rgba(8, 33, 45, .90), rgba(4, 20, 29, .94));
  }

  .member-card {
    display: flex;
    gap: 70px;
    padding: 24px;
    margin: 18px 0;
  }

  .small { color: #829aa8; margin-bottom: 8px; }
  .level, .amount { color: #fff; font-size: 24px; font-weight: 600; }
  .amount { color: #00c8f4; }
  .expire { color: #d7edf4; font-size: 16px; font-weight: 600; }

  .plan {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
    padding: 25px;
  }
  .plan h3 { color: #fff; }
  .price { color: #00c8f4; font-size: 28px; font-weight: 600; margin: 12px 0; }
  .price span { font-size: 14px; color: #829aa8; }
  .benefits { white-space: pre-line; line-height: 1.8; color: #9eb1bc; }

  .orders { margin-top: 24px; padding: 18px; }
  .section-title { margin-bottom: 14px; color: #00c8f4; font-size: 16px; font-weight: 600; }
}

::v-deep {
  .el-alert { background: #3a2d14; border: 1px solid rgba(230, 162, 60, .45); color: #f3c66b; }
  .el-table { color: #d7edf4; background: transparent; }
  .el-table th, .el-table tr, .el-table td { background: #08212d !important; border-color: rgba(65, 104, 124, .38) !important; }
  .el-table th { color: #8fa8b6; }
  .el-table__body tr:hover > td { background: #0d3040 !important; }
  .el-table::before, .el-table--border::after, .el-table--group::after { background: rgba(65, 104, 124, .38); }
}
</style>
